import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { createCompany, getMyCompany, updateCompany } from '@/lib/api';
import { Building2, Loader2, Plus, X } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const MEXICAN_STATES = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas',
  'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima', 'Durango', 'Estado de México',
  'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Michoacán', 'Morelos', 'Nayarit',
  'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí',
  'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'
];

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const CompanyForm = ({ isNew = false }) => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { isUSA } = useLocale();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [certificationInput, setCertificationInput] = useState('');
  const [newService, setNewService] = useState('');
  const [formData, setFormData] = useState({
    company_name: '',
    legal_name: '',
    company_type: '',
    industry: '',
    description: '',
    location_state: '',
    location_city: '',
    contact_person: '',
    phone: '',
    email: user?.email || '',
    website: '',
    certifications: [],
    production_capacity: '',
    avg_delivery_time: '',
    logo_url: '',
    cover_url: '',
    photos: [],
    offered_services: [],
    // Categorías múltiples
    is_proveedor: false,
    is_maquilero: false,
    is_comprador: false,
    // Subcategorías de proveedor
    proveedor_telas: false,
    proveedor_avios: false,
    proveedor_etiquetas: false,
    proveedor_hilos: false,
    proveedor_empaque: false,
    proveedor_otros: false,
    // Subcategorías de maquilero
    maquilero_corte: false,
    maquilero_confeccion: false,
    maquilero_acabados: false,
    maquilero_bordado: false,
    maquilero_estampado: false,
    maquilero_tenido: false,
    maquilero_otros: false,
    // Subcategorías de comprador
    comprador_fabricante: false,
    comprador_revendedor: false,
    comprador_tienda: false,
    comprador_marca: false,
    comprador_exportador: false,
    comprador_otros: false
  });

  useEffect(() => {
    if (!isNew) {
      const fetchCompany = async () => {
        setLoading(true);
        try {
          const company = await getMyCompany();
          setFormData({
            company_name: company.company_name || '',
            legal_name: company.legal_name || '',
            company_type: company.company_type || '',
            industry: company.industry || '',
            description: company.description || '',
            location_state: company.location_state || '',
            location_city: company.location_city || '',
            contact_person: company.contact_person || '',
            phone: company.phone || '',
            email: company.email || '',
            website: company.website || '',
            certifications: company.certifications || [],
            production_capacity: company.production_capacity || '',
            avg_delivery_time: company.avg_delivery_time || '',
            logo_url: company.logo_url || '',
            cover_url: company.cover_url || '',
            photos: company.photos || [],
            offered_services: company.offered_services || [],
            // Categorías múltiples
            is_proveedor: company.is_proveedor || false,
            is_maquilero: company.is_maquilero || false,
            is_comprador: company.is_comprador || false,
            // Subcategorías de proveedor
            proveedor_telas: company.proveedor_telas || false,
            proveedor_avios: company.proveedor_avios || false,
            proveedor_etiquetas: company.proveedor_etiquetas || false,
            proveedor_hilos: company.proveedor_hilos || false,
            proveedor_empaque: company.proveedor_empaque || false,
            proveedor_otros: company.proveedor_otros || false,
            // Subcategorías de maquilero
            maquilero_corte: company.maquilero_corte || false,
            maquilero_confeccion: company.maquilero_confeccion || false,
            maquilero_acabados: company.maquilero_acabados || false,
            maquilero_bordado: company.maquilero_bordado || false,
            maquilero_estampado: company.maquilero_estampado || false,
            maquilero_tenido: company.maquilero_tenido || false,
            maquilero_otros: company.maquilero_otros || false,
            // Subcategorías de comprador
            comprador_fabricante: company.comprador_fabricante || false,
            comprador_revendedor: company.comprador_revendedor || false,
            comprador_tienda: company.comprador_tienda || false,
            comprador_marca: company.comprador_marca || false,
            comprador_exportador: company.comprador_exportador || false,
            comprador_otros: company.comprador_otros || false
          });
        } catch (error) {
          if (isNew) {
            // Expected for new companies
          } else {
            toast.error(isUSA ? 'Error loading company data' : 'Error al cargar datos de la empresa');
          }
        } finally {
          setLoading(false);
        }
      };
      fetchCompany();
    }
  }, [isNew]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isNew) {
        const company = await createCompany(formData);
        updateUser({ company_id: company.company_id });
        toast.success(isUSA ? 'Company registered successfully' : 'Empresa registrada correctamente');
        navigate('/dashboard');
      } else {
        await updateCompany(user.company_id, formData);
        toast.success(isUSA ? 'Company updated successfully' : 'Empresa actualizada correctamente');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const addCertification = () => {
    if (certificationInput.trim()) {
      setFormData({
        ...formData,
        certifications: [...formData.certifications, certificationInput.trim()]
      });
      setCertificationInput('');
    }
  };

  const removeCertification = (index) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.filter((_, i) => i !== index)
    });
  };

  const addService = () => {
    if (newService.trim() && !formData.offered_services.includes(newService.trim())) {
      setFormData({
        ...formData,
        offered_services: [...formData.offered_services, newService.trim()]
      });
      setNewService('');
    }
  };

  const removeService = (index) => {
    setFormData({
      ...formData,
      offered_services: formData.offered_services.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="company-form">
      <div className="page-header">
        <h1 className="page-title">
          {isNew 
            ? (isUSA ? 'Register company' : 'Registrar empresa') 
            : (isUSA ? 'Edit company' : 'Editar empresa')}
        </h1>
        <p className="page-subtitle">
          {isNew 
            ? (isUSA ? 'Complete your company information to start using TexHub' : 'Completa los datos de tu empresa para comenzar a usar TexHub')
            : (isUSA ? 'Update your company information' : 'Actualiza la información de tu empresa')}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="card-industrial mb-6">
          <CardHeader>
            <CardTitle>{isUSA ? 'Basic information' : 'Información básica'}</CardTitle>
            <CardDescription>{isUSA ? 'Main company details' : 'Datos principales de tu empresa'}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">{isUSA ? 'Company name *' : 'Nombre comercial *'}</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder={isUSA ? 'ABC Textiles' : 'Textiles ABC'}
                  required
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="logo_upload">{isUSA ? 'Company logo' : 'Logo de la empresa'}</Label>
                <div className="flex items-center gap-3">
                  {formData.logo_url && (
                    <img 
                      src={formData.logo_url} 
                      alt="Logo" 
                      className="w-12 h-12 object-contain rounded border"
                    />
                  )}
                  <Input
                    id="logo_upload"
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 2 * 1024 * 1024) {
                          toast.error(isUSA ? 'Logo must be less than 2MB' : 'El logo debe ser menor a 2MB');
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setFormData({ ...formData, logo_url: event.target.result });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="cursor-pointer"
                  />
                </div>
                <p className="text-xs text-slate-500">{isUSA ? 'Upload your company logo (max. 2MB)' : 'Sube el logo de tu empresa (máx. 2MB)'}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="legal_name">{isUSA ? 'Legal name' : 'Razón social'}</Label>
                <Input
                  id="legal_name"
                  value={formData.legal_name}
                  onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                  placeholder={isUSA ? 'ABC Textiles Inc.' : 'Textiles ABC S.A. de C.V.'}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isUSA ? 'Company categories *' : 'Categorías de tu empresa *'}</Label>
                <p className="text-sm text-slate-500 mb-3">{isUSA ? 'Select all that apply' : 'Selecciona todas las que apliquen'}</p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Checkbox 
                      id="is_proveedor"
                      checked={formData.is_proveedor}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_proveedor: checked })}
                      data-testid="checkbox-proveedor"
                    />
                    <div className="grid gap-0.5 leading-none">
                      <Label htmlFor="is_proveedor" className="cursor-pointer font-medium">
                        {isUSA ? 'Supplier' : 'Proveedor'}
                      </Label>
                      <p className="text-xs text-slate-500">{isUSA ? 'I sell materials, fabrics, trims or supplies' : 'Vendo materiales, telas, avíos o insumos'}</p>
                    </div>
                  </div>
                  
                  {/* Subcategorías de Proveedor */}
                  {formData.is_proveedor && (
                    <div className="ml-7 p-3 bg-blue-50 rounded-lg space-y-2">
                      <p className="text-xs font-medium text-blue-700 mb-2">{isUSA ? 'What type of products do you sell?' : '¿Qué tipo de productos vendes?'}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="proveedor_telas"
                            checked={formData.proveedor_telas}
                            onCheckedChange={(checked) => setFormData({ ...formData, proveedor_telas: checked })}
                          />
                          <Label htmlFor="proveedor_telas" className="text-sm cursor-pointer">{isUSA ? 'Fabrics' : 'Telas'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="proveedor_avios"
                            checked={formData.proveedor_avios}
                            onCheckedChange={(checked) => setFormData({ ...formData, proveedor_avios: checked })}
                          />
                          <Label htmlFor="proveedor_avios" className="text-sm cursor-pointer">{isUSA ? 'Trims' : 'Avíos'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="proveedor_etiquetas"
                            checked={formData.proveedor_etiquetas}
                            onCheckedChange={(checked) => setFormData({ ...formData, proveedor_etiquetas: checked })}
                          />
                          <Label htmlFor="proveedor_etiquetas" className="text-sm cursor-pointer">{isUSA ? 'Labels' : 'Etiquetas'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="proveedor_hilos"
                            checked={formData.proveedor_hilos}
                            onCheckedChange={(checked) => setFormData({ ...formData, proveedor_hilos: checked })}
                          />
                          <Label htmlFor="proveedor_hilos" className="text-sm cursor-pointer">{isUSA ? 'Threads & fibers' : 'Hilos y fibras'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="proveedor_empaque"
                            checked={formData.proveedor_empaque}
                            onCheckedChange={(checked) => setFormData({ ...formData, proveedor_empaque: checked })}
                          />
                          <Label htmlFor="proveedor_empaque" className="text-sm cursor-pointer">{isUSA ? 'Packaging' : 'Empaque'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="proveedor_otros"
                            checked={formData.proveedor_otros}
                            onCheckedChange={(checked) => setFormData({ ...formData, proveedor_otros: checked })}
                          />
                          <Label htmlFor="proveedor_otros" className="text-sm cursor-pointer">{isUSA ? 'Other' : 'Otros'}</Label>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-3">
                    <Checkbox 
                      id="is_maquilero"
                      checked={formData.is_maquilero}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_maquilero: checked })}
                      data-testid="checkbox-maquilero"
                    />
                    <div className="grid gap-0.5 leading-none">
                      <Label htmlFor="is_maquilero" className="cursor-pointer font-medium">
                        {isUSA ? 'Manufacturing Partner / Services' : 'Maquilero / Servicios'}
                      </Label>
                      <p className="text-xs text-slate-500">{isUSA ? 'I offer sewing, cutting, embroidery services, etc.' : 'Ofrezco servicios de confección, corte, bordado, etc.'}</p>
                    </div>
                  </div>
                  
                  {/* Subcategorías de Maquilero */}
                  {formData.is_maquilero && (
                    <div className="ml-7 p-3 bg-purple-50 rounded-lg space-y-2">
                      <p className="text-xs font-medium text-purple-700 mb-2">{isUSA ? 'What services do you offer?' : '¿Qué servicios ofreces?'}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="maquilero_corte"
                            checked={formData.maquilero_corte}
                            onCheckedChange={(checked) => setFormData({ ...formData, maquilero_corte: checked })}
                          />
                          <Label htmlFor="maquilero_corte" className="text-sm cursor-pointer">{isUSA ? 'Cutting' : 'Corte'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="maquilero_confeccion"
                            checked={formData.maquilero_confeccion}
                            onCheckedChange={(checked) => setFormData({ ...formData, maquilero_confeccion: checked })}
                          />
                          <Label htmlFor="maquilero_confeccion" className="text-sm cursor-pointer">{isUSA ? 'Sewing' : 'Confección'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="maquilero_acabados"
                            checked={formData.maquilero_acabados}
                            onCheckedChange={(checked) => setFormData({ ...formData, maquilero_acabados: checked })}
                          />
                          <Label htmlFor="maquilero_acabados" className="text-sm cursor-pointer">{isUSA ? 'Finishing' : 'Acabados'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="maquilero_bordado"
                            checked={formData.maquilero_bordado}
                            onCheckedChange={(checked) => setFormData({ ...formData, maquilero_bordado: checked })}
                          />
                          <Label htmlFor="maquilero_bordado" className="text-sm cursor-pointer">{isUSA ? 'Embroidery' : 'Bordado'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="maquilero_estampado"
                            checked={formData.maquilero_estampado}
                            onCheckedChange={(checked) => setFormData({ ...formData, maquilero_estampado: checked })}
                          />
                          <Label htmlFor="maquilero_estampado" className="text-sm cursor-pointer">{isUSA ? 'Printing' : 'Estampado'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="maquilero_tenido"
                            checked={formData.maquilero_tenido}
                            onCheckedChange={(checked) => setFormData({ ...formData, maquilero_tenido: checked })}
                          />
                          <Label htmlFor="maquilero_tenido" className="text-sm cursor-pointer">{isUSA ? 'Dyeing' : 'Teñido'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="maquilero_otros"
                            checked={formData.maquilero_otros}
                            onCheckedChange={(checked) => setFormData({ ...formData, maquilero_otros: checked })}
                          />
                          <Label htmlFor="maquilero_otros" className="text-sm cursor-pointer">{isUSA ? 'Other' : 'Otros'}</Label>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-3">
                    <Checkbox 
                      id="is_comprador"
                      checked={formData.is_comprador}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_comprador: checked })}
                      data-testid="checkbox-comprador"
                    />
                    <div className="grid gap-0.5 leading-none">
                      <Label htmlFor="is_comprador" className="cursor-pointer font-medium">
                        {isUSA ? 'Buyer' : 'Comprador'}
                      </Label>
                      <p className="text-xs text-slate-500">{isUSA ? 'I buy materials (clothing manufacturer, reseller, etc.)' : 'Compro materiales (fabricante de ropa, revendedor, etc.)'}</p>
                    </div>
                  </div>
                  
                  {/* Subcategorías de Comprador */}
                  {formData.is_comprador && (
                    <div className="ml-7 p-3 bg-green-50 rounded-lg space-y-2">
                      <p className="text-xs font-medium text-green-700 mb-2">{isUSA ? 'What type of buyer are you?' : '¿Qué tipo de comprador eres?'}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="comprador_fabricante"
                            checked={formData.comprador_fabricante}
                            onCheckedChange={(checked) => setFormData({ ...formData, comprador_fabricante: checked })}
                          />
                          <Label htmlFor="comprador_fabricante" className="text-sm cursor-pointer">{isUSA ? 'Clothing manufacturer' : 'Fabricante de ropa'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="comprador_revendedor"
                            checked={formData.comprador_revendedor}
                            onCheckedChange={(checked) => setFormData({ ...formData, comprador_revendedor: checked })}
                          />
                          <Label htmlFor="comprador_revendedor" className="text-sm cursor-pointer">{isUSA ? 'Reseller' : 'Revendedor'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="comprador_tienda"
                            checked={formData.comprador_tienda}
                            onCheckedChange={(checked) => setFormData({ ...formData, comprador_tienda: checked })}
                          />
                          <Label htmlFor="comprador_tienda" className="text-sm cursor-pointer">{isUSA ? 'Clothing store' : 'Tienda de ropa'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="comprador_marca"
                            checked={formData.comprador_marca}
                            onCheckedChange={(checked) => setFormData({ ...formData, comprador_marca: checked })}
                          />
                          <Label htmlFor="comprador_marca" className="text-sm cursor-pointer">{isUSA ? 'Own brand' : 'Marca propia'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="comprador_exportador"
                            checked={formData.comprador_exportador}
                            onCheckedChange={(checked) => setFormData({ ...formData, comprador_exportador: checked })}
                          />
                          <Label htmlFor="comprador_exportador" className="text-sm cursor-pointer">{isUSA ? 'Exporter' : 'Exportador'}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="comprador_otros"
                            checked={formData.comprador_otros}
                            onCheckedChange={(checked) => setFormData({ ...formData, comprador_otros: checked })}
                          />
                          <Label htmlFor="comprador_otros" className="text-sm cursor-pointer">{isUSA ? 'Other' : 'Otros'}</Label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="industry">{isUSA ? 'Industry / Sector *' : 'Giro / Industria *'}</Label>
                <Select
                  value={formData.industry}
                  onValueChange={(value) => setFormData({ ...formData, industry: value })}
                  required
                >
                  <SelectTrigger data-testid="industry-select">
                    <SelectValue placeholder={isUSA ? 'Select industry' : 'Seleccionar giro'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hilos_fibras">{isUSA ? 'Threads & Fibers' : 'Hilos y Fibras'}</SelectItem>
                    <SelectItem value="telas">{isUSA ? 'Fabrics' : 'Telas'}</SelectItem>
                    <SelectItem value="avios">{isUSA ? 'Trims' : 'Avíos'}</SelectItem>
                    <SelectItem value="procesos">{isUSA ? 'Textile Processes' : 'Procesos Textiles'}</SelectItem>
                    <SelectItem value="maquila">{isUSA ? 'Manufacturing & Sewing' : 'Maquila y Confección'}</SelectItem>
                    <SelectItem value="producto_terminado">{isUSA ? 'Finished Product' : 'Producto Terminado'}</SelectItem>
                    <SelectItem value="servicios">{isUSA ? 'Complementary Services' : 'Servicios Complementarios'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{isUSA ? 'Description' : 'Descripción'}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={isUSA ? 'Describe your company, main products or services...' : 'Describe tu empresa, productos o servicios principales...'}
                rows={4}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="card-industrial mb-6">
          <CardHeader>
            <CardTitle>{isUSA ? 'Location and contact' : 'Ubicación y contacto'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location_state">{isUSA ? 'State *' : 'Estado *'}</Label>
                <Select
                  value={formData.location_state}
                  onValueChange={(value) => setFormData({ ...formData, location_state: value })}
                  required
                >
                  <SelectTrigger data-testid="state-select">
                    <SelectValue placeholder={isUSA ? 'Select state' : 'Seleccionar estado'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(isUSA ? US_STATES : MEXICAN_STATES).map((state) => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="location_city">{isUSA ? 'City *' : 'Ciudad *'}</Label>
                <Input
                  id="location_city"
                  value={formData.location_city}
                  onChange={(e) => setFormData({ ...formData, location_city: e.target.value })}
                  placeholder={isUSA ? 'City' : 'Ciudad'}
                  required
                  data-testid="city-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_person">{isUSA ? 'Contact person *' : 'Persona de contacto *'}</Label>
                <Input
                  id="contact_person"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  placeholder={isUSA ? 'Full name' : 'Nombre completo'}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{isUSA ? 'Phone *' : 'Teléfono *'}</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder={isUSA ? '+1 555 123 4567' : '+52 55 1234 5678'}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">{isUSA ? 'Contact email *' : 'Email de contacto *'}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={isUSA ? 'contact@company.com' : 'contacto@empresa.com'}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">{isUSA ? 'Website' : 'Sitio web'}</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder={isUSA ? 'https://www.company.com' : 'https://www.empresa.com'}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-industrial mb-6">
          <CardHeader>
            <CardTitle>{isUSA ? 'Capabilities and certifications' : 'Capacidades y certificaciones'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="production_capacity">{isUSA ? 'Production capacity' : 'Capacidad de producción'}</Label>
                <Input
                  id="production_capacity"
                  value={formData.production_capacity}
                  onChange={(e) => setFormData({ ...formData, production_capacity: e.target.value })}
                  placeholder={isUSA ? 'E.g.: 10,000 meters/month' : 'Ej: 10,000 metros/mes'}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="avg_delivery_time">{isUSA ? 'Average delivery time' : 'Tiempo de entrega promedio'}</Label>
                <Input
                  id="avg_delivery_time"
                  value={formData.avg_delivery_time}
                  onChange={(e) => setFormData({ ...formData, avg_delivery_time: e.target.value })}
                  placeholder={isUSA ? 'E.g.: 15-20 days' : 'Ej: 15-20 días'}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{isUSA ? 'Certifications' : 'Certificaciones'}</Label>
              <div className="flex gap-2">
                <Input
                  value={certificationInput}
                  onChange={(e) => setCertificationInput(e.target.value)}
                  placeholder="ISO 9001, OEKO-TEX, etc."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
                />
                <Button type="button" variant="outline" onClick={addCertification}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.certifications.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.certifications.map((cert, index) => (
                    <span key={index} className="badge bg-slate-100 text-slate-700 border-slate-200 flex items-center gap-1">
                      {cert}
                      <button type="button" onClick={() => removeCertification(index)}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Services Section - only for fabricantes/maquileros */}
        {formData.is_maquilero && (
          <Card className="card-industrial mb-6">
            <CardHeader>
              <CardTitle>{isUSA ? 'Services you offer' : 'Servicios que ofreces'}</CardTitle>
              <CardDescription>
                {isUSA 
                  ? 'Define the manufacturing or process services you offer. Customers can select from this list when requesting a quote.'
                  : 'Define los servicios de maquila o procesos que ofreces. Los clientes podrán seleccionar de esta lista al solicitar cotización.'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newService}
                  onChange={(e) => setNewService(e.target.value)}
                  placeholder={isUSA ? 'E.g.: Fabric cutting, Sewing, Embroidery, Printing...' : 'Ej: Corte de tela, Confección, Bordado, Estampado...'}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                />
                <Button type="button" variant="outline" onClick={addService}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.offered_services.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.offered_services.map((service, index) => (
                    <span key={index} className="badge bg-purple-100 text-purple-700 border-purple-200 flex items-center gap-1 py-1 px-3">
                      {service}
                      <button type="button" onClick={() => removeService(index)} className="hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 p-3 bg-slate-50 rounded-lg">
                  {isUSA 
                    ? 'Add the services you offer so customers can request quotes directly.'
                    : 'Agrega los servicios que ofreces para que los clientes puedan cotizar directamente.'
                  }
                </p>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="card-industrial mb-6">
          <CardHeader>
            <CardTitle>{isUSA ? 'Images' : 'Imágenes'}</CardTitle>
            <CardDescription>{isUSA ? 'Image URLs for your profile' : 'URLs de imágenes para tu perfil'}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="logo_url">{isUSA ? 'Logo (URL)' : 'Logo (URL)'}</Label>
              <Input
                id="logo_url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                placeholder={isUSA ? 'https://example.com/logo.png' : 'https://ejemplo.com/logo.png'}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cover_url">{isUSA ? 'Cover image (URL)' : 'Imagen de portada (URL)'}</Label>
              <Input
                id="cover_url"
                value={formData.cover_url}
                onChange={(e) => setFormData({ ...formData, cover_url: e.target.value })}
                placeholder={isUSA ? 'https://example.com/cover.jpg' : 'https://ejemplo.com/portada.jpg'}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            {isUSA ? 'Cancel' : 'Cancelar'}
          </Button>
          <Button 
            type="submit" 
            className="bg-emerald-600 hover:bg-emerald-700"
            disabled={saving}
            data-testid="save-company-btn"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isUSA ? 'Saving...' : 'Guardando...'}
              </>
            ) : (
              isNew 
                ? (isUSA ? 'Register company' : 'Registrar empresa') 
                : (isUSA ? 'Save changes' : 'Guardar cambios')
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CompanyForm;
